import React, { useEffect, useState } from 'react';
import { getData } from '../../../shopping-cart/cart-bridger-pay/data/data-service';
import { CartSteps } from '../../../shopping-cart/cart-bridger-pay/enums/cart-steps';
import Preloader from '../../../../ui/svg/common/preloader';
import CartItems from './cart-items';
import EmptyCart from './empty-cart';
import ProductPriceFormat from '../../../products/product-price/product-price-format';
import { IStoreProps } from '../index';
import { sendGAEvent } from '../../../../domain/marketing/gtm-provider';
import {
    ConfirmationMsgVisibleReferer, IConfirmationMsgVisibleResult,
    isConfirmationMsgVisible
} from '../../../shopping-cart/cart-bridger-pay/domain/free-plugin-provider';
import FreePluginBox from '../../../shopping-cart/cart-bridger-pay/ui/free-plugin/free-plugin-box';
import { isEqual } from 'lodash';
import UpsellBox from '../../../../domain/upsell/upsell-box/upsell-box';
import { UpsellReferrer } from '../../../../domain/upsell/interfaces';
import FreePluginBoxMilestones
    from '../../../shopping-cart/cart-bridger-pay/ui/free-plugin/free-plugin-box-milestones/free-plugin-box-milestones';

interface ITopMiniCartDropPanel {
    storeProps: IStoreProps;
}

const TopMiniCartDropPanel = ({ storeProps }: ITopMiniCartDropPanel) => {

    const { useAppSelector, rootActions, useAppDispatch } = storeProps;

    const dispatch = useAppDispatch();

    // const cartData = useAppSelector(store => store.root.cartData);
    const cartData = useAppSelector(
        store => store.root.cartData,
        isEqual
    );

    const cartItems = cartData?.cartItems.filter(cartItem => cartItem.isActive);
    const isLoading = useAppSelector(store => store.root.isLoading);
    const showItems = !isLoading && !!cartData && !!cartItems && cartItems.length > 0;
    const showEmptyCart = !isLoading && (!cartItems || cartItems.length <= 0);

    const [showAddMoreBox, setShowAddMoreBox] = useState(false);
    const [addMoreBox, setAddMoreBox] = useState<IConfirmationMsgVisibleResult|null>(null);

    /**
     * page load
     */
    useEffect(() => {

        (async () => {
            dispatch(
                rootActions.main({
                    isLoading: true,
                })
            );

            const response = await getData({
                step: CartSteps.First,
            });

            dispatch(
                rootActions.main({
                    isLoading: false,
                })
            );

            if(!response || response.statusCode !== 200 || !response.data.isValid || !response.data.relatedData){
                return;
            }

            dispatch(
                rootActions.setCartData(response.data.relatedData)
            );
        })();
    },
    [dispatch, rootActions]);

    /**
     * https://wavesaudio.atlassian.net/browse/DSWEB-5070
     * https://wavesaudio.atlassian.net/browse/MRKT-27499
     */
    useEffect(() => {
        if(!cartData || showEmptyCart) return;

        // https://wavesaudio.atlassian.net/browse/DSWEB-5070
        // https://wavesaudio.atlassian.net/browse/MRKT-27499
        const confirmationMessageVisibility = isConfirmationMsgVisible(
            cartData,
            ConfirmationMsgVisibleReferer.TopMinicart
        );

        const addMoreEnabled = confirmationMessageVisibility.addMoreEnabled;
        const progressBarEnabled = confirmationMessageVisibility.progressBarEnabled;
        const isEnabled = addMoreEnabled || progressBarEnabled;

        if(!isEnabled) return;
        
        setShowAddMoreBox(isEnabled);
        setAddMoreBox(confirmationMessageVisibility);
    }, [
        cartData,
        showEmptyCart,
    ]);

    return (
        <div
            className="top-mini-cart-drop absolute right-0 top-full bg-white text-gray-500 shadow-md rounded py-4 px-2 text-tiny border border-gray-100"
            style={{
                maxWidth: 'calc(100vw - 8px)',
            }}>

            {
                isLoading &&
                <div className="flex justify-center my-8">
                    <Preloader width="25px" height="25px" color="#4f4f4f" />
                </div>
            }

            { showEmptyCart && <EmptyCart storeProps={ storeProps } /> }

            {
                showItems && (
                    <>
                        <CartItems storeProps={ storeProps } />

                        {
                            showAddMoreBox && !!addMoreBox &&
                            <FreePluginBox
                                visibility={ addMoreBox as IConfirmationMsgVisibleResult }
                                cartData={ cartData }
                                classes="mx-2"
                            />
                        }

                        <FreePluginBoxMilestones
                            cartData={ cartData }
                            classes="mx-2"
                            referrer={ ConfirmationMsgVisibleReferer.TopMinicart }
                            cartStep={ 1 }
                        />

                        <UpsellBox
                            cartData={ cartData }
                            classes="mt-4 mx-2"
                            referrer={ UpsellReferrer.TopMiniCart }
                        />

                        <div className="flex flex-col mt-4 mb-2 px-2">
                            {
                                cartData.priceData.totalDiscountPrice > 0 &&
                                <div className="text-right text-green font-bold">
                                    Discount: -<ProductPriceFormat price={ cartData.priceData.totalDiscountPrice } />
                                </div>
                            }

                            <div className="flex justify-between items-center">
                                <a
                                    className="w-btn w-auto"
                                    href="/cart"
                                    onClick={ () => {
                                        sendGAEvent('Top Mini Cart', 'View Cart & Checkout');
                                    }}>View Cart & Checkout</a>

                                <div className="text-xl">
                                    <span className="mr-2">Total:</span>
                                    <span className="font-medium"><ProductPriceFormat price={ cartData.priceData.totalPrice } /></span>
                                </div>
                            </div>
                        </div>
                    </>
                )
            }
        </div>
    )
};

export default TopMiniCartDropPanel;